<template>
    <div>
        <div class="tw:flex tw:items-center tw:justify-between tw:mb-1">
            <span class="tw:text-sm tw:font-medium tw:text-gray-700">Etiqueta</span>
            <Button size="sm" variant="ghost" @click="showModal('crear-etiqueta')">
                <i class="fas fa-plus tw:mr-1"></i> Crear
            </Button>
        </div>
        <BaseMultiselect
            :value="etiquetaId"
            :options="etiquetas"
            label-key="name"
            value-key="id"
            placeholder="Seleccionar etiqueta"
            :multiple="false"
            :loading="loadingEtiquetas"
            @input="onSelect"
        >
            <template #noOptions>No hay etiquetas disponibles</template>
        </BaseMultiselect>
        <CrearEtiqueta
            :etiqueta="{}"
            @close="closeModal('crear-etiqueta')"
            @crear="crearEtiquetaDesdeModal"
        />
    </div>
</template>

<script setup>
    import { ref, computed, onMounted, watch } from 'vue'
    import { useModal } from '@/composables/common/useModal'
    import { useEtiquetas } from '@/composables/admin/etiquetas/useEtiquetas'
    import CrearEtiqueta from '@/modules/admin/etiquetas/modals/CrearEtiqueta.vue'
    import BaseMultiselect from '@/modules/admin/ui/BaseMultiselect.vue'
    import Button from '@/modules/admin/ui/Button.vue'

    const emit = defineEmits(['seleccionada', 'input', 'creada'])

    const modal = useModal()

    // Carga, alta y feedback viven en el composable: este componente sólo
    // renderiza las opciones y emite la selección.
    const { etiquetas, loading: loadingEtiquetas, loadEtiquetas, createEtiqueta } = useEtiquetas()

    // Contrato controlado: el padre es la única fuente de verdad de la
    // selección. `value` es el objeto etiqueta (o null) y `input` lo emite al
    // cambiar. Cuando el padre resetea su `value` (p. ej. resetForm() de
    // BloquearButacas), este watch empuja el reset al multiselect y la UI queda
    // visualmente vacía, no sólo el ref del padre.
    const props = defineProps({
        value: {
            type: Object,
            default: null
        }
    })

    const etiqueta = ref(props.value || null)

    const etiquetaId = computed(() => etiqueta.value?.id ?? null)

    watch(() => props.value, newVal => {
        etiqueta.value = newVal || null
    })

    watch(etiqueta, newVal => {
        emit('seleccionada', newVal || null)
        emit('input', newVal || null)
    })

    // BaseMultiselect emite el `value-key` (id), no el objeto. Traducimos al
    // objeto antes de propagar para mantener el contrato del componente.
    function onSelect(id) {
        etiqueta.value = id ? (etiquetas.value.find(e => e.id === id) || null) : null
    }

    /**
     * Alta desde el modal. El composable recarga el catálogo una sola vez; aquí
     * sólo se cierra la ventana y se notifica la etiqueta creada, si se resolvió.
     */
    function crearEtiquetaDesdeModal(etiquetaData) {
        return createEtiqueta(etiquetaData)
            .then(creada => {
                closeModal('crear-etiqueta')

                if (creada) {
                    emit('creada', creada)
                }

                return creada
            })
            .catch(error => {
                // Sólo se cierra ante una respuesta definitiva del backend: un
                // fallo de red deja el formulario utilizable para reintentar.
                if (error.response) {
                    closeModal('crear-etiqueta')
                }
                throw error
            })
    }

    function showModal(name) {
        modal.show(name)
    }

    // Ocultar y resetear la selección. NO recarga el catálogo: la recarga es
    // responsabilidad del composable tras un alta, y hacerlo aquí generaba dos
    // peticiones concurrentes que podían sobrescribir la lista.
    function closeModal(name) {
        etiqueta.value = null
        modal.hide(name)
    }

    onMounted(() => {
        loadEtiquetas()
    })
</script>
